
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Edit, Trash2, Plus, RefreshCw } from 'lucide-react';
import { useCurrencies, useDeleteCurrency, Currency } from '@/hooks/useCurrencies';
import { CurrencyDialog } from './CurrencyDialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';

export function CurrenciesTable() {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingCurrency, setEditingCurrency] = useState<Currency | null>(null);
  
  const { data: currencies, isLoading, refetch } = useCurrencies();
  const deleteCurrency = useDeleteCurrency();

  const handleEdit = (currency: Currency) => {
    setEditingCurrency(currency);
    setDialogOpen(true);
  };

  const handleDelete = (id: string) => {
    deleteCurrency.mutate(id);
  };

  const handleAddNew = () => {
    setEditingCurrency(null);
    setDialogOpen(true);
  };

  const handleRefresh = () => {
    refetch();
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('ar-SA', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="text-center">
          <RefreshCw className="mx-auto h-8 w-8 animate-spin text-gray-400" />
          <p className="mt-2 text-sm text-gray-500">جاري تحميل العملات...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-2">
          <h3 className="text-lg font-semibold">قائمة العملات</h3>
          <Badge variant="secondary" className="text-xs">
            {currencies?.length || 0} عملة
          </Badge>
        </div>
        <div className="flex gap-2">
          <Button onClick={handleRefresh} variant="outline" size="sm">
            <RefreshCw className="h-4 w-4 mr-1" />
            تحديث الأسعار
          </Button>
          <Button onClick={handleAddNew} size="sm">
            <Plus className="h-4 w-4 mr-1" />
            إضافة عملة
          </Button>
        </div>
      </div>

      <div className="border rounded-lg overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-gray-50">
              <TableHead className="text-right">الاسم بالعربية</TableHead>
              <TableHead className="text-right">الاسم بالإنجليزية</TableHead>
              <TableHead className="text-center">كود ISO</TableHead>
              <TableHead className="text-center">هامش السعر %</TableHead>
              <TableHead className="text-center">سعر التحويل إلى ريال</TableHead>
              <TableHead className="text-center">سعر التحويل من ريال</TableHead>
              <TableHead className="text-center">آخر تحديث</TableHead>
              <TableHead className="text-center">الإجراءات</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {currencies?.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} className="text-center py-8 text-gray-500">
                  لا توجد عملات مضافة
                </TableCell>
              </TableRow>
            ) : (
              currencies?.map((currency) => (
                <TableRow key={currency.id} className="hover:bg-gray-50">
                  <TableCell className="font-medium">{currency.name_ar}</TableCell>
                  <TableCell className="text-gray-600">{currency.name_en}</TableCell>
                  <TableCell className="text-center">
                    <Badge variant="outline" className="font-mono">
                      {currency.iso_code}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-center">
                    <span className="text-sm font-medium">
                      {currency.price_margin}%
                    </span>
                  </TableCell>
                  <TableCell className="text-center font-mono text-sm">
                    {currency.rate_to_sar.toFixed(4)}
                  </TableCell>
                  <TableCell className="text-center font-mono text-sm">
                    {currency.rate_from_sar.toFixed(4)}
                  </TableCell>
                  <TableCell className="text-center text-xs text-gray-500">
                    {formatDate(currency.last_updated)}
                  </TableCell>
                  <TableCell className="text-center">
                    <div className="flex justify-center gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleEdit(currency)}
                        className="h-8 w-8 p-0"
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                      {currency.iso_code !== 'SAR' && (
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-8 w-8 p-0 text-red-600 hover:text-red-700"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>تأكيد الحذف</AlertDialogTitle>
                              <AlertDialogDescription>
                                هل أنت متأكد من حذف عملة "{currency.name_ar}"؟ لا يمكن التراجع عن هذا الإجراء.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>إلغاء</AlertDialogCancel>
                              <AlertDialogAction
                                onClick={() => handleDelete(currency.id)}
                                className="bg-red-600 hover:bg-red-700"
                              >
                                حذف
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      <CurrencyDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        currency={editingCurrency}
      />
    </div>
  );
}
